// 派生指標と表示単位への換算（§5.5 / §5.6）。
// 重要: 表示単位の切替は EngineResult（base 値）に対する純粋変換のみ。再シミュレーションしない。
import { fromBase } from '../currency/convert';
import type { RateTable } from '../currency/rate-table';
import type { EngineResult } from '../core/types';

export function runsPerHour(t: number): number {
  return t > 0 ? 3600 / t : 0;
}

/** 期待時給（base）= EV × runs/h。 */
export function expectedHourlyBase(r: EngineResult, t: number): number {
  return r.EV * runsPerHour(t);
}

/** 最低時給（base）= (最低収入 − C) × runs/h。運が最悪でも保証される floor 収入（§5.5）。 */
export function minHourlyBase(r: EngineResult, t: number): number {
  return (r.minIncome - r.C) * runsPerHour(t);
}

/** 表示単位へ換算した出力（金額系のみ換算、単位不変な値はそのまま）。 */
export interface DisplayMetrics {
  // 金額系（表示単位。レート未取得なら null）
  income: number | null;
  minIncome: number | null;
  EV: number | null;
  bankFull: number | null;
  bankHalf: number | null;
  bankQuarter: number | null;
  expectedHourly: number | null;
  minHourly: number | null;
  maxB: number | null;
  curve: Array<{ B: number; p: number }>; // B は表示単位
  // 単位不変（§5.6）
  fStar: number;
  roi: number;
  growthHalf: number;
  ruinFull: number;
  ruinHalf: number;
  ruinQuarter: number;
}

export function toDisplayUnit(
  r: EngineResult,
  t: number,
  displayUnit: string,
  rt: RateTable,
): DisplayMetrics {
  const conv = (base: number): number | null => fromBase(base, displayUnit, rt);
  return {
    income: conv(r.income),
    minIncome: conv(r.minIncome),
    EV: conv(r.EV),
    bankFull: conv(r.bankFull),
    bankHalf: conv(r.bankHalf),
    bankQuarter: conv(r.bankQuarter),
    expectedHourly: conv(expectedHourlyBase(r, t)),
    minHourly: conv(minHourlyBase(r, t)),
    maxB: conv(r.maxB),
    curve: r.curve.map((pt) => ({ B: conv(pt.B) ?? 0, p: pt.p })),
    fStar: r.fStar,
    roi: r.roi,
    growthHalf: r.growthHalf,
    ruinFull: r.ruinFull,
    ruinHalf: r.ruinHalf,
    ruinQuarter: r.ruinQuarter,
  };
}
